/* 상태창 디자인 템플릿 — 작가가 만든 항목(개수 · 종류 아무거나)에 맞춰 HTML 을 조립한다. 고른 뒤 HTML 을 직접 고쳐도 된다.
   그리는 곳은 스크립트 없는 샌드박스(custom-screen.tsx): 쓸 수 있는 건 HTML · CSS 와 {{항목}} 글자 치환뿐이라,
   - 숫자 게이지는 CSS calc 로 폭을 낸다: ({{값}} - 최소) * 100% / (최대 - 최소). 값이 숫자가 아니면 그 선언이 버려져 폭 0 이 된다
   - 단계형은 {{항목.단계}}(몇 번째)로 칸마다 인라인 opacity 를 계산해 지금 단계까지 칠한다(stepHtml)
   - 공개되면 서버가 HTML 을 정리한다(sai-backend authoring.py sanitize_screen): <style> 은 인라인 style 로 바뀌고, 허용 속성(색 · 배경 · 테두리 ·
     여백 · flex · 크기 · 글자 · opacity · box-shadow …) 밖의 것과 CSS 변수 · 속성 선택자 · ::before/::after · transform · mask 는 사라진다.
     그래서 포인트색은 변수 대신 값으로 박고(solid), 꾸밈(꺾쇠 · 테이프 · 하트 모양)은 빠져도 읽히게 둔다. 스튜디오 미리보기는 정리 전 모습이다(2026-10-13)
   - 속마음 · 생각처럼 이름에 마음이 든 글 항목은 따로 뺀다(말풍선 · 메모지 · 인용)
   AI 생성이 아니라 규칙 조립이다(2차 원칙: 작가 입력이 기본).

   2026-10-07 전면 교체 — 예전 5종은 바탕을 안 칠하거나 흰색 반투명에 기대 밝은 바탕 위에서 글이 사라졌고, 디자인도 "촌스럽고 AI 같다"로 거절.
   규칙: 모든 템플릿은 자기 바탕 · 글자색을 스스로 칠한다(어떤 장면 · 테마 위에서도 읽힌다). 밝은 면은 쓰지 않는다(앱 다크 원칙).
   결은 1020 여성향 로맨스 대화 앱에 맞췄다 — 웹소설 시스템창 · 오토메 호감도 하트 · 다꾸 다이어리 · 휴대폰 위젯 · 한 줄 바. */
import type { StatusField } from '@/features/studio-renew/types'

type StatusAccent = { id: string; label: string; color: string }
export const STATUS_ACCENTS: StatusAccent[] = [
  { id: 'cherry', label: '체리', color: '#ff5c8a' },
  { id: 'lavender', label: '라벤더', color: '#a98bff' },
  { id: 'coral', label: '코랄', color: '#ff6a55' },
  { id: 'mint', label: '민트', color: '#3fd0b0' },
  { id: 'sky', label: '스카이', color: '#5aaeff' },
  { id: 'butter', label: '버터', color: '#f2c14e' },
]

/** HTML 글자 · 속성 안 */
const esc = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const MIND = /속마음|생각|독백|마음|메모/

type Parts = {
  gauges: StatusField[]
  steps: StatusField[]
  picks: StatusField[]
  texts: StatusField[]
  mind?: StatusField
}

function split(fields: StatusField[]): Parts {
  const shown = fields.filter((f) => !f.hidden && f.key.trim())
  const mind = shown.find((f) => f.type === 'text' && MIND.test(f.key))
  return {
    gauges: shown.filter((f) => f.type === 'number' && f.max != null),
    steps: shown.filter(
      (f) => f.type === 'list' && f.ordered && f.options.length > 1
    ),
    picks: shown.filter(
      (f) => f.type === 'list' && !(f.ordered && f.options.length > 1)
    ),
    // 최댓값이 없는 숫자는 채움 비율을 낼 수 없어 값만 보여 준다
    texts: shown.filter(
      (f) =>
        (f.type === 'text' && f !== mind) ||
        (f.type === 'number' && f.max == null)
    ),
    mind,
  }
}

const v = (f: StatusField) => `{{${f.key}}}`
/** 게이지 채움 비율(0~100 숫자) */
function pct(f: StatusField) {
  const min = f.min ?? 0
  const span = Math.max(1, (f.max ?? min) - min)
  return `max(0,min(100,(${v(f)} - ${min}) * 100 / ${span}))`
}
/** 게이지 폭(%) */
const width = (f: StatusField) => `calc(${pct(f)} * 1%)`
const gaugeMax = (f: StatusField) =>
  f.max != null ? `<small>/${f.max}</small>` : ''

/**
 * 단계형 — 지금 단계까지 칸을 칠하고 지금 단계 이름만 밝힌다.
 * 서버가 HTML 을 정리하며 속성 선택자([data-v=…]) · CSS 변수 · ::before 를 지운다(sai-backend authoring.py sanitize_screen, 2026-10-13 확인) —
 * 그래서 CSS 규칙 대신 앱이 채우는 {{항목.단계}}(몇 번째 · 1부터)로 칸마다 인라인 opacity 를 계산한다(lib/custom-screen.ts stepNumbers).
 * 칸 k: 단계 ≥ k+1 이면 1, 아니면 0.22. 이름 j: 단계 = j+1 일 때만 1 — 차이의 제곱으로 abs() 없이 판정한다
 */
const stepHtml = (f: StatusField, a: string) => {
  const n = `{{${f.key}.단계}}`
  const pip = (k: number) =>
    `<i style="background:${a};opacity:calc(0.22 + 0.78 * clamp(0, ${n} - ${k}, 1))"></i>`
  const label = (o: string, j: number) =>
    `<span style="color:#fff;opacity:calc(0.45 + 0.55 * clamp(0, 1 - (${n} - ${j + 1}) * (${n} - ${j + 1}), 1))">${esc(o)}</span>`
  return `<div class="st"><div class="sk">${esc(f.key)}</div><div class="pips">${f.options.map((_, k) => pip(k)).join('')}</div><div class="ls">${f.options.map(label).join('')}</div></div>`
}

/** 포인트색 — CSS 변수(--a)는 서버 정리에서 지워져 색이 사라진다. 고른 색 값을 그대로 박는다 */
const solid = (html: string, a: string) => html.replaceAll('var(--a)', a)

/** 모든 템플릿 공통 글꼴 — 샌드박스 안이라 앱 글꼴 파일을 못 읽는다. 설치된 Pretendard → 시스템 글꼴 */
const FONT =
  'Pretendard,"Apple SD Gothic Neo","Malgun Gothic",system-ui,sans-serif'

/** 하트 한 칸(가로 16 · 세로 14) — 게이지 · 단계 칸을 하트 모양으로 오려 낸다(mask). 색은 CSS 가 칠하므로 그림 안에 색이 없다 */
const HEART = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 14'%3E%3Cpath d='M8 13.6C3.4 10.4 0 7.6 0 4.3 0 1.9 1.9 0 4.2 0 5.8 0 7.2.9 8 2.3 8.8.9 10.2 0 11.8 0 14.1 0 16 1.9 16 4.3c0 3.3-3.4 6.1-8 9.3z'/%3E%3C/svg%3E")`

type StatusTheme = {
  id: string
  name: string
  desc: string
  render: (fields: StatusField[], accent: string) => string
}

/* ---------- 1. 시스템창 — 웹소설 속 '띠링' 상태창. 남색 반투명 창 · 모서리 꺾쇠 · 칸 나뉜 빛 게이지 ---------- */
const system: StatusTheme = {
  id: 'system',
  name: '시스템창',
  desc: '웹소설 속 띠링 상태창',
  render(fields, a) {
    const p = split(fields)
    const css = `.w{--a:${a};position:relative;padding:14px 16px 12px;border-radius:3px;background:linear-gradient(180deg,#121a38,#0a0f24);color:#eaf0ff;font:13px/1.55 ${FONT};box-shadow:0 0 0 1px color-mix(in srgb,var(--a) 55%,transparent),0 0 26px -6px color-mix(in srgb,var(--a) 55%,transparent),inset 0 0 34px -12px color-mix(in srgb,var(--a) 40%,transparent)}
.w:before,.w:after{content:"";position:absolute;width:12px;height:12px;border:2px solid var(--a)}.w:before{top:-2px;left:-2px;border-right:0;border-bottom:0}.w:after{right:-2px;bottom:-2px;border-left:0;border-top:0}
.hd{margin-bottom:8px;text-align:center;font-weight:700;letter-spacing:.08em;color:var(--a);text-shadow:0 0 10px color-mix(in srgb,var(--a) 70%,transparent)}
.row{display:flex;justify-content:space-between;gap:12px;padding:5px 0;border-bottom:1px solid rgba(255,255,255,.07)}.k{color:color-mix(in srgb,var(--a) 45%,#cfd8f5)}.val{font-weight:600;color:#fff;text-align:right}
.g{padding:7px 0 4px}.gh{display:flex;justify-content:space-between;align-items:baseline}.gh b{color:#fff;font-variant-numeric:tabular-nums}.gh small{color:#8b93b3;font-weight:500}
.bar{position:relative;height:8px;margin-top:6px;background:rgba(255,255,255,.08);box-shadow:inset 0 0 0 1px color-mix(in srgb,var(--a) 28%,transparent)}.bar i{display:block;height:100%;background:linear-gradient(90deg,color-mix(in srgb,var(--a) 55%,#fff),var(--a));box-shadow:0 0 10px var(--a)}
.bar:after{content:"";position:absolute;inset:0;background:repeating-linear-gradient(90deg,transparent 0 calc(10% - 2px),#0d1330 calc(10% - 2px) 10%)}
.pick{display:inline-block;margin:8px 6px 0 0;padding:2px 9px;background:color-mix(in srgb,var(--a) 16%,transparent);box-shadow:inset 0 0 0 1px color-mix(in srgb,var(--a) 55%,transparent);color:#fff;font-size:12px}
.st{padding:7px 0 2px}.sk{color:color-mix(in srgb,var(--a) 45%,#cfd8f5)}.pips{display:flex;gap:4px;margin:6px 0 4px}.pips i{flex:1;height:6px;background:rgba(255,255,255,.1);transform:skewX(-24deg)}.ls{display:flex;justify-content:space-between;font-size:11px;color:#7d86a8}
.mind{margin-top:10px;padding:8px 10px;border-left:2px solid var(--a);background:color-mix(in srgb,var(--a) 10%,transparent);color:#dfe6ff;font-style:italic}`
    const html = [
      `<div class="hd">[ 상태창 ]</div>`,
      ...p.texts.map(
        (f) =>
          `<div class="row"><span class="k">${esc(f.key)}</span><span class="val">${v(f)}</span></div>`
      ),
      ...p.gauges.map(
        (f) =>
          `<div class="g"><div class="gh"><span class="k">${esc(f.key)}</span><b>${v(f)}${gaugeMax(f)}</b></div><div class="bar"><i style="width:${width(f)}"></i></div></div>`
      ),
      ...p.steps.map((f) => stepHtml(f, a)),
      p.picks.length
        ? `<div>${p.picks.map((f) => `<span class="pick">${esc(f.key)} : ${v(f)}</span>`).join('')}</div>`
        : '',
      p.mind ? `<div class="mind">${v(p.mind)}</div>` : '',
    ].join('\n')
    return solid(`<style>${css}</style>\n<div class="w">\n${html}\n</div>`, a)
  },
}

/* ---------- 2. 하트 시그널 — 연애 시뮬레이션 결. 포인트색 테두리 · 하트로 채워지는 호감 게이지 · 말풍선 속마음 ---------- */
const otome: StatusTheme = {
  id: 'otome',
  name: '하트 시그널',
  desc: '하트로 차오르는 연애 시뮬 창',
  render(fields, a) {
    const p = split(fields)
    const css = `.w{--a:${a};padding:1.5px;border-radius:20px;background:linear-gradient(135deg,var(--a),color-mix(in srgb,var(--a) 35%,#2a1830) 60%,color-mix(in srgb,var(--a) 70%,#fff))}
.in{border-radius:18.5px;padding:14px 16px;background:radial-gradient(110% 70% at 100% 0%,color-mix(in srgb,var(--a) 20%,transparent),transparent 62%),#1a1219;color:#fff3f7;font:13px/1.55 ${FONT}}
.row{display:flex;justify-content:space-between;gap:12px;padding:4px 0}.k{color:color-mix(in srgb,var(--a) 45%,#f3dbe5);font-weight:600}.val{font-weight:600;text-align:right}
.g{display:flex;align-items:center;gap:10px;padding:6px 0}.g .k{flex:none;min-width:44px}.g b{margin-left:auto;font-variant-numeric:tabular-nums}
.hearts{width:160px;max-width:100%;height:14px;background:rgba(255,255,255,.16);-webkit-mask:${HEART} 0 0/16px 14px repeat-x;mask:${HEART} 0 0/16px 14px repeat-x}.hearts i{display:block;height:100%;background:var(--a)}
.pick{display:inline-block;margin:8px 6px 0 0;padding:3px 10px;border-radius:99px;background:color-mix(in srgb,var(--a) 24%,transparent);color:#fff;font-size:12px;font-weight:600}
.st{padding:6px 0 2px}.sk{color:color-mix(in srgb,var(--a) 45%,#f3dbe5);font-weight:600}.pips{display:flex;gap:6px;margin:6px 0 3px}.pips i{width:16px;height:14px;background:rgba(255,255,255,.16);-webkit-mask:${HEART} center/contain no-repeat;mask:${HEART} center/contain no-repeat}.ls{display:flex;flex-wrap:wrap;gap:4px 10px;font-size:11px;color:#a58f9a}
.mind{position:relative;margin-top:10px;padding:9px 12px;border-radius:14px 14px 14px 4px;background:rgba(255,255,255,.08);color:#ffe8f0}.mind:before{content:"♥ ";color:var(--a)}`
    const html = [
      ...p.gauges.map(
        (f) =>
          `<div class="g"><span class="k">${esc(f.key)}</span><div class="hearts"><i style="width:${width(f)}"></i></div><b>${v(f)}</b></div>`
      ),
      ...p.steps.map((f) => stepHtml(f, a)),
      ...p.texts.map(
        (f) =>
          `<div class="row"><span class="k">${esc(f.key)}</span><span class="val">${v(f)}</span></div>`
      ),
      p.picks.length
        ? `<div>${p.picks.map((f) => `<span class="pick">${esc(f.key)} ${v(f)}</span>`).join('')}</div>`
        : '',
      p.mind ? `<div class="mind">${v(p.mind)}</div>` : '',
    ].join('\n')
    return solid(
      `<style>${css}</style>\n<div class="w"><div class="in">\n${html}\n</div></div>`,
      a
    )
  },
}

/* ---------- 3. 다꾸 다이어리 — 모눈 다이어리에 마스킹테이프 · 라벨 스티커 · 형광펜 게이지 · 포스트잇 속마음 ---------- */
const diary: StatusTheme = {
  id: 'diary',
  name: '다꾸 다이어리',
  desc: '테이프 · 스티커 · 포스트잇',
  render(fields, a) {
    const p = split(fields)
    const css = `.w{--a:${a};position:relative;margin-top:9px;padding:18px 16px 14px;border-radius:6px 6px 16px 6px;background:radial-gradient(rgba(255,255,255,.09) 1px,transparent 1.3px) 0 0/14px 14px,#211d23;color:#f4eef3;font:13px/1.6 ${FONT};box-shadow:0 12px 24px -14px rgba(0,0,0,.7),inset 0 0 0 1px rgba(255,255,255,.05)}
.w:before{content:"";position:absolute;top:-9px;left:18px;width:78px;height:18px;background:repeating-linear-gradient(45deg,color-mix(in srgb,var(--a) 80%,transparent) 0 6px,color-mix(in srgb,var(--a) 58%,transparent) 6px 12px);transform:rotate(-4deg)}
.row{display:flex;align-items:baseline;gap:8px;padding:3px 0}.k{flex:none;padding:0 7px;border-radius:5px;background:color-mix(in srgb,var(--a) 30%,#2b2530);color:#fff;font-size:12px;font-weight:700}.val{min-width:0}
.g{display:flex;align-items:center;gap:8px;padding:5px 0}.g .k{min-width:0}.bar{flex:1;height:11px;border-radius:3px;background:rgba(255,255,255,.07)}.bar i{display:block;height:100%;border-radius:3px;background:color-mix(in srgb,var(--a) 72%,transparent);transform:skewX(-14deg)}.g b{font-variant-numeric:tabular-nums}
.pick{display:inline-block;margin:8px 6px 0 0;padding:1px 9px;border-radius:99px;border:1.5px dashed color-mix(in srgb,var(--a) 75%,transparent);color:#fff;font-size:12px;font-weight:600}
.st{padding:6px 0 2px}.sk{display:inline-block;padding:0 7px;border-radius:5px;background:color-mix(in srgb,var(--a) 30%,#2b2530);font-size:12px;font-weight:700}.pips{display:flex;gap:6px;margin:7px 0 3px}.pips i{width:12px;height:12px;border-radius:50%;box-shadow:inset 0 0 0 1.5px color-mix(in srgb,var(--a) 80%,transparent)}.ls{display:flex;flex-wrap:wrap;gap:4px 10px;font-size:11px;color:#9d929c}
.mind{margin:12px 2px 2px;padding:9px 11px;background:color-mix(in srgb,var(--a) 24%,#2a2530);color:#fff;transform:rotate(-1deg);box-shadow:0 8px 16px -10px rgba(0,0,0,.7)}`
    const html = [
      ...p.texts.map(
        (f) =>
          `<div class="row"><span class="k">${esc(f.key)}</span><span class="val">${v(f)}</span></div>`
      ),
      ...p.gauges.map(
        (f) =>
          `<div class="g"><span class="k">${esc(f.key)}</span><div class="bar"><i style="width:${width(f)}"></i></div><b>${v(f)}</b></div>`
      ),
      ...p.steps.map((f) => stepHtml(f, a)),
      p.picks.length
        ? `<div>${p.picks.map((f) => `<span class="pick">${esc(f.key)} ${v(f)}</span>`).join('')}</div>`
        : '',
      p.mind ? `<div class="mind">${v(p.mind)}</div>` : '',
    ].join('\n')
    return solid(`<style>${css}</style>\n<div class="w">\n${html}\n</div>`, a)
  },
}

/* ---------- 4. 위젯 — 휴대폰 홈 화면 위젯. 칸마다 큰 값 · 숫자는 둥근 링 게이지 ---------- */
const widget: StatusTheme = {
  id: 'widget',
  name: '위젯',
  desc: '홈 화면 위젯 · 링 게이지',
  render(fields, a) {
    const p = split(fields)
    const css = `.w{--a:${a};display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;color:#f5f5f7;font:12px/1.45 ${FONT}}
.t{min-width:0;border-radius:18px;padding:11px 12px;background:linear-gradient(160deg,#27272c,#18181c);box-shadow:inset 0 1px 0 rgba(255,255,255,.07)}.t.wide{grid-column:1/-1}
.k{color:#a1a1aa;font-weight:600}.big{margin-top:3px;overflow:hidden;font-size:16px;font-weight:700;letter-spacing:-.01em;text-overflow:ellipsis;white-space:nowrap}
.g{display:flex;align-items:center;gap:10px}.g .big{color:var(--a);font-variant-numeric:tabular-nums}.g>div{min-width:0}
.ring{flex:none;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,.1);-webkit-mask:radial-gradient(farthest-side,transparent calc(100% - 5px),#000 calc(100% - 5px));mask:radial-gradient(farthest-side,transparent calc(100% - 5px),#000 calc(100% - 5px))}
.sk{color:#a1a1aa;font-weight:600}.pips{display:flex;gap:4px;margin:8px 0 4px}.pips i{flex:1;height:5px;border-radius:9px;background:rgba(255,255,255,.1)}.ls{display:flex;justify-content:space-between;font-size:10px;color:#71717a}
.mind{grid-column:1/-1;border-radius:18px;padding:11px 12px;background:linear-gradient(160deg,color-mix(in srgb,var(--a) 30%,#1f1f24),#18181c);font-size:13px}.mind .k{color:var(--a)}`
    const html = [
      ...p.gauges.map(
        (f) =>
          // 링 — 값이 숫자가 아니면 conic 선언이 버려져 빈 링(앞의 background)만 남는다
          `<div class="t g"><span class="ring" style="background:conic-gradient(var(--a) calc(${pct(f)} * 1%),rgba(255,255,255,.1) 0)"></span><div><div class="k">${esc(f.key)}</div><div class="big">${v(f)}</div></div></div>`
      ),
      ...p.texts.map(
        (f) =>
          `<div class="t"><div class="k">${esc(f.key)}</div><div class="big">${v(f)}</div></div>`
      ),
      ...p.picks.map(
        (f) =>
          `<div class="t"><div class="k">${esc(f.key)}</div><div class="big">${v(f)}</div></div>`
      ),
      ...p.steps.map((f) => `<div class="t wide">${stepHtml(f, a)}</div>`),
      p.mind
        ? `<div class="mind"><div class="k">${esc(p.mind.key)}</div>${v(p.mind)}</div>`
        : '',
    ].join('\n')
    return solid(`<style>${css}</style>\n<div class="w">\n${html}\n</div>`, a)
  },
}

/* ---------- 5. 한 줄 바 — 대화를 가리지 않는 얇은 줄. 몰입형 ---------- */
const strip: StatusTheme = {
  id: 'strip',
  name: '한 줄 바',
  desc: '대화를 가리지 않는 얇은 줄',
  render(fields, a) {
    const p = split(fields)
    const css = `.w{--a:${a};display:flex;flex-wrap:wrap;align-items:center;gap:6px 14px;padding:8px 14px;border-radius:14px;background:#151519;box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);color:#f5f5f7;font:12px/1.5 ${FONT}}
.c{display:inline-flex;align-items:center;gap:6px;white-space:nowrap}.k{color:#a1a1aa}.val{font-weight:700}
.mini{width:40px;height:4px;border-radius:9px;background:rgba(255,255,255,.14);overflow:hidden}.mini i{display:block;height:100%;border-radius:9px;background:var(--a)}
.dot{width:6px;height:6px;border-radius:50%;background:var(--a);box-shadow:0 0 8px var(--a)}
.mind{flex-basis:100%;padding-top:6px;border-top:1px solid rgba(255,255,255,.08);color:#d4d4d8;font-style:italic}`
    const html = [
      ...p.texts.map(
        (f) =>
          `<span class="c"><span class="k">${esc(f.key)}</span><span class="val">${v(f)}</span></span>`
      ),
      ...p.gauges.map(
        (f) =>
          `<span class="c"><span class="k">${esc(f.key)}</span><span class="mini"><i style="width:${width(f)}"></i></span><span class="val">${v(f)}</span></span>`
      ),
      ...[...p.steps, ...p.picks].map(
        (f) =>
          `<span class="c"><span class="dot"></span><span class="val">${v(f)}</span></span>`
      ),
      p.mind ? `<span class="mind">${v(p.mind)}</span>` : '',
    ].join('\n')
    return solid(`<style>${css}</style>\n<div class="w">\n${html}\n</div>`, a)
  },
}

export const STATUS_THEMES: StatusTheme[] = [
  otome,
  system,
  diary,
  widget,
  strip,
]
